/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useFeatureFlag } from '@/features/globalConfig';
import { createQuery } from '@/utils/createReactQuery';
import { keepPreviousData, type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { getThirdPartyAxios } from '@/lib/axios/thirdPartyClient';
import {
  type GetNearByStoresQueryParams,
  type GetNearByStoreResponse,
} from '../interface/yext-types';
import { type GetNearByStoreQueryParams, type NearbyStoreSearchParams } from '../interface/types';
import { useLocale } from '@/hooks/useLocale';
import { getYextCountryCodes } from '../utils/getYextCountryCodes';
import { type Locale } from '@/types/i18n';

type GetNearByStoresOptions = {
  yextURL: string;
  yextAPIKey: string;
  yextVersion: string;
  locale: Locale;
  storeId: string;
  limit: number;
  searchParams: NearbyStoreSearchParams | undefined;
};

const getNearByStores = async (options: GetNearByStoresOptions) => {
  const { yextURL, yextAPIKey, yextVersion, locale, storeId, searchParams, limit } = options;

  const missingYextParams: string[] = [];

  if (!searchParams) {
    return null;
  }

  if (!yextURL) {
    missingYextParams.push('YEXT_URL');
  }
  if (!yextAPIKey) {
    missingYextParams.push('YEXT_API_KEY');
  }
  if (!yextVersion) {
    missingYextParams.push('YEXT_VERSION');
  }

  if (missingYextParams.length > 0) {
    throw new Error(`Missing Yext parameters in globalConfig: ${missingYextParams.join(', ')}`);
  }

  const filters = {
    countryCode: { $in: getYextCountryCodes(locale) },
    'meta.id': { '!$eq': storeId },
    closed: { $eq: false },
    slug: { '!$eq': '' },
  };

  const params: GetNearByStoresQueryParams = {
    entityTypes: 'location',
    limit,
    fields: [
      'name',
      'address',
      'mainPhone',
      'hours',
      'timezone',
      'closed',
      'slug',
      'yextDisplayCoordinate',
      'googlePlaceId',
      'websiteUrl',
    ].join(','),
    location: '',
    filter: JSON.stringify(filters),
    v: yextVersion,
  };

  if (searchParams.type === 'coordinates') {
    params.location = [searchParams.latitude, searchParams.longitude].join(',');

    if (searchParams.radius !== undefined) {
      params.radius = Math.max(0.1, Math.min(searchParams.radius, 2500));
    }
  }
  if (searchParams.type === 'address') {
    params.location = searchParams.address;
  }
  params.filter = JSON.stringify(filters);

  const base = yextURL.endsWith('/') ? yextURL : `${yextURL}/`;
  const url = new URL('me/entities/geosearch', base).toString();
  const response = await getThirdPartyAxios().get<GetNearByStoreResponse>(url, {
    params,
    headers: {
      'api-key': yextAPIKey,
    },
  });

  return response.data;
};

const { useData, prefetch, getFromCache } = createQuery('nearByStores', getNearByStores);

export const useNearByStores = (options: GetNearByStoreQueryParams) => {
  const { searchParams, storeId = '', limit, enabled = true } = options;

  const yextURL = useFeatureFlag('YEXT_URL') ?? '';
  const yextAPIKey = useFeatureFlag('YEXT_API_KEY') ?? '';
  const yextVersion = useFeatureFlag('YEXT_VERSION') ?? '';

  const locale = useLocale();

  const isValidSearchParams = () => {
    if (!searchParams) {
      return false;
    }
    if (searchParams.type === 'coordinates') {
      return searchParams.latitude && searchParams.longitude;
    } else {
      return searchParams.address;
    }
  };

  return useData({
    yextURL,
    yextAPIKey,
    yextVersion,
    locale,
    storeId,
    searchParams,
    limit,
    enabled: Boolean(yextURL && yextAPIKey && yextVersion && enabled && isValidSearchParams()),
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const prefetchStoreDetails = (
  queryClient: QueryClient,
  axios: AxiosInstance,
  options: GetNearByStoresOptions,
) => {
  return prefetch(queryClient, options, axios);
};

export const getStoreDetailsFromCache = (
  queryClient: QueryClient,
  options: GetNearByStoresOptions,
) => {
  return getFromCache(queryClient, options);
};
